import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import {
  getComparisonTargets,
  groupFacilitiesByLocation,
  calculateLocationStats,
  generateLocationRankings,
  createServiceCoverageHeatmap,
  createCapacityRankingOption,
  createServiceComparisonOption,
} from '../../utils/comparisonUtils'

function HealthComparison({
  facilities,
  locations,
  selectedDistrict,
  selectedSubcounty,
  selectedParish,
  selectedVillage,
  isViewingAllDistricts,
  districtFacilityGroups,
  sortBy,
  sortOrder,
  showTopN,
  maxLocationDepth,
}) {
  // Determine what locations to compare
  const comparisonConfig = useMemo(() => {
    if (isViewingAllDistricts() && districtFacilityGroups) {
      // Multi-district mode: compare districts
      const districtTargets = Object.keys(districtFacilityGroups).map(
        districtName => ({
          id: districtName.toLowerCase(),
          name: districtName,
          code: `UG.${districtName.toUpperCase().slice(0, 3)}`,
        })
      )

      return {
        targets: districtTargets,
        type: 'districts',
        context: 'Comparing all districts',
        level: 'district',
      }
    }

    return getComparisonTargets(
      locations,
      selectedDistrict,
      selectedSubcounty,
      selectedParish,
      selectedVillage,
      maxLocationDepth
    )
  }, [
    locations,
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    isViewingAllDistricts,
    districtFacilityGroups,
    maxLocationDepth,
  ])

  // Group facilities by comparison targets and calculate stats
  const locationStats = useMemo(() => {
    if (!comparisonConfig.targets.length || !facilities.length) return []

    if (isViewingAllDistricts() && districtFacilityGroups) {
      // Custom grouping for district comparison
      return comparisonConfig.targets
        .map(target => {
          const districtFacilities = districtFacilityGroups[target.name] || []
          const locationData = {
            id: target.id,
            name: target.name,
            facilities: districtFacilities,
            code: target.code,
          }
          return calculateLocationStats(locationData)
        })
        .filter(stats => stats.facilityCount > 0) // Only include districts with facilities
    }

    const grouped = groupFacilitiesByLocation(
      facilities,
      comparisonConfig.targets,
      comparisonConfig.level
    )
    return grouped.map(calculateLocationStats)
  }, [
    facilities,
    comparisonConfig,
    isViewingAllDistricts,
    districtFacilityGroups,
  ])

  // Generate rankings with current sort settings
  const rankings = useMemo(() => {
    if (!locationStats.length) return []
    return generateLocationRankings(locationStats, sortBy, sortOrder).slice(
      0,
      showTopN
    )
  }, [locationStats, sortBy, sortOrder, showTopN])

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!locationStats.length) return null

    return {
      serviceHeatmap: createServiceCoverageHeatmap(locationStats),
    }
  }, [locationStats])

  // Chart options
  const capacityChartOption = useMemo(() => {
    if (!rankings.length) return null
    return createCapacityRankingOption(rankings, 'Healthcare Capacity Ranking')
  }, [rankings])

  const serviceComparisonOption = useMemo(() => {
    if (!locationStats.length) return null
    return createServiceComparisonOption(
      locationStats,
      'Service Coverage Comparison'
    )
  }, [locationStats])

  return (
    <>
      {/* Healthcare Capacity Ranking - Full Width */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Healthcare Capacity Ranking
        </h3>
        <div className="h-80">
          {capacityChartOption && (
            <ReactECharts
              option={capacityChartOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            Which areas have the most healthcare infrastructure?
          </p>
          <p className="mt-2">
            Rankings based on facility count and composite healthcare delivery
            score.
          </p>
        </div>
      </div>

      {/* Service Coverage Comparison - Full Width */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Service Coverage Comparison
        </h3>
        <div className="h-96">
          {serviceComparisonOption && (
            <ReactECharts
              option={serviceComparisonOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            Where can residents access different types of care?
          </p>
          <p className="mt-2">
            Percentage of facilities offering key services across locations.
          </p>
        </div>
      </div>

      {/* Service Coverage Heatmap */}
      {chartData?.serviceHeatmap && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            🔥 Service Coverage Heatmap
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="text-left text-sm font-medium text-gray-700 p-2">
                    Location
                  </th>
                  {chartData.serviceHeatmap.services.map((service, index) => (
                    <th
                      key={index}
                      className="text-center text-sm font-medium text-gray-700 p-2 min-w-24"
                    >
                      <div className="flex flex-col items-center">
                        <span className="text-lg">{service.icon}</span>
                        <span className="text-xs mt-1">
                          {service.name.split(' ')[0]}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chartData.serviceHeatmap.locations.map(
                  (location, locationIndex) => (
                    <tr key={locationIndex} className="border-t">
                      <td className="text-sm font-medium text-gray-900 p-2">
                        {location}
                      </td>
                      {chartData.serviceHeatmap.services.map(
                        (service, serviceIndex) => {
                          const cellData = chartData.serviceHeatmap.data.find(
                            d =>
                              d.locationIndex === locationIndex &&
                              d.serviceIndex === serviceIndex
                          )
                          return (
                            <td key={serviceIndex} className="text-center p-2">
                              <div
                                className="w-16 h-8 rounded flex items-center justify-center text-white text-xs font-medium mx-auto"
                                style={{
                                  backgroundColor: cellData?.color || '#6b7280',
                                }}
                                title={`${cellData?.serviceName}: ${cellData?.percentage || 0}%`}
                              >
                                {cellData?.percentage || 0}%
                              </div>
                            </td>
                          )
                        }
                      )}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">Service availability across locations</p>
            <p className="flex items-center space-x-4 mt-2">
              <span className="flex items-center">
                <span className="inline-block w-3 h-3 bg-green-500 rounded mr-1"></span>
                Good (≥80%)
              </span>
              <span className="flex items-center">
                <span className="inline-block w-3 h-3 bg-yellow-500 rounded mr-1"></span>
                Moderate (50-79%)
              </span>
              <span className="flex items-center">
                <span className="inline-block w-3 h-3 bg-red-500 rounded mr-1"></span>
                Poor (&lt;50%)
              </span>
            </p>
          </div>
        </div>
      )}
    </>
  )
}

HealthComparison.propTypes = {
  facilities: PropTypes.arrayOf(PropTypes.object).isRequired,
  locations: PropTypes.object.isRequired,
  selectedDistrict: PropTypes.string,
  selectedSubcounty: PropTypes.string,
  selectedParish: PropTypes.string,
  selectedVillage: PropTypes.string,
  isViewingAllDistricts: PropTypes.func.isRequired,
  districtFacilityGroups: PropTypes.object,
  sortBy: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  showTopN: PropTypes.number.isRequired,
  maxLocationDepth: PropTypes.string.isRequired,
}

export default HealthComparison
